import * as React from "react";
import { useEffect, useState } from "react";
import { fetchData } from "@/types";
import Infinity from "@/components/atomics/infinity";
import ShowIcon from "@/components/atomics/showIcon";
import HideIcon from "@/components/atomics/hideIcon";
import { CollectionStandardData } from "@/types/collection";
import { checkIpfsUrl } from "@/components/utils/checkUrl";
import Clock from "@/components/atomics/clock";
import { getFileStorageUrl, getFileStorageUrlSync } from "@/utils/storage";

interface CollectionCardType {
  onClick?: () => void;
  className: string;
  data_url?: string;
  value?: number;
  totalMintCount: number;
  currentMintedCount: number;
  isEditView?: boolean;
  mint_time_limit?: number;
  is_hidden?: boolean;
  is_owner_edit?: boolean;
}

export default function CollectionCard({
  className,
  data_url,
  value,
  totalMintCount,
  currentMintedCount,
  onClick,
  isEditView,
  mint_time_limit,
  is_hidden,
  is_owner_edit,
}: CollectionCardType) {
  const [collectionData, setCollectionData] =
    useState<CollectionStandardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isTimeExpired, setIsTimeExpired] = useState(false);
  const [hasMintTimeLimit, setHasMintTimeLimit] = useState(false);
  const [displayImageUrl, setDisplayImageUrl] = useState<string>();

  useEffect(() => {
    if (data_url) {
      fetchData(data_url).then(data => {
        setCollectionData(data);
      });
    }
  }, [data_url]);

  useEffect(() => {
    if (mint_time_limit) {
      const mintTimeDate = new Date(mint_time_limit);
      const defaultDate = new Date("1970-01-01T00:00:00.000Z");

      if (mintTimeDate.getTime() !== defaultDate.getTime()) {
        setHasMintTimeLimit(true);
        const currentDate = new Date();
        if (mintTimeDate < currentDate) {
          setIsTimeExpired(true);
        }
      }
    }
  }, [mint_time_limit]);

  useEffect(() => {
    const validateImage = async () => {
      if (collectionData?.image) {
        setIsLoading(true);
        const imageUrl = await getFileStorageUrl(collectionData.image);
        const isValid =
          imageUrl === collectionData.image
            ? await checkIpfsUrl(collectionData.image)
            : true;
        if (isValid) {
          setDisplayImageUrl(imageUrl);
          setImageLoaded(true);
          setIsLoading(false);
        } else {
          setImageLoaded(false);
          setIsLoading(false);
        }
      }
    };

    validateImage();
  }, [collectionData?.image]);

  const formatName = (name: string) => {
    if (name.length <= 9) return name;
    return `${name.slice(0, 4)}...${name.slice(-3)}`;
  };

  const getMintCount = () => {
    if (isTimeExpired) return currentMintedCount;
    if (totalMintCount === 0)
      return <Infinity className="w-[18px] h-[18px]" color="#000000" />;
    return Math.max(totalMintCount, currentMintedCount);
  };

  return (
    <div className={`relative ${className}`} onClick={onClick}>
      <div className="w-full aspect-square">
        {isLoading ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-[24px] line-height-20 bg-[#525252]">
            <p>LOADING</p>
            <p>MEDIA</p>
          </div>
        ) : imageLoaded ? (
          <img src={displayImageUrl} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#525252]"></div>
        )}
      </div>
      <h1 className="font-silkscreen text-[20px] text-black truncate">
        {collectionData?.name}
      </h1>
      <p className="font-silkscreen text-[20px] --bold text-[#0099FE]">
        {value} TON
      </p>
      <div className="flex items-center text-[16px] text-black">
        <span className="text-black">{currentMintedCount}/</span>
        <span className="text-black">{getMintCount()}</span>
      </div>
      {isEditView && (
        <div className="absolute top-0 right-0 bg-black p-2">
          {is_hidden ? (
            <HideIcon className="w-[28px] h-[22px]" onClick={() => {}} />
          ) : (
            <ShowIcon className="w-[28px] h-[22px]" onClick={() => {}} />
          )}
        </div>
      )}
      {hasMintTimeLimit && (
        <div className="absolute bottom-0 right-0">
          <Clock className="w-[24px] h-[24px]" />
        </div>
      )}
    </div>
  );
}
